import { requestStatus, OUTSTANDING_FORM_STATUSES } from 'src/utils/formRequestDecisions';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Chip, IconButton, Tooltip, Stack, alpha } from '@mui/material';
import Iconify from '../../../components/Iconify';
import { common } from '../../../theme/palette';

// ─── Date helpers ──────────────────────────────────────────────────────────────
const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const formatDate = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.format('DD MMM YYYY, h:mm A') : '—';
};

// ─── Status configuration ──────────────────────────────────────────────────────
// Exported so SEND_CONSENT_FORM checklist cells can reuse the same colors + icons.
export const STATUS_CONFIG = {
    PENDING:     { label: 'Pending',     color: '#FFAB00', bg: 'rgba(255,171,0,0.12)',    icon: 'eva:clock-fill' },
    OPENED:      { label: 'Opened',      color: '#078DEE', bg: 'rgba(7,141,238,0.12)',    icon: 'eva:eye-fill' },
    IN_PROGRESS: { label: 'In Progress', color: '#855CF8', bg: 'rgba(133,92,248,0.12)',   icon: 'eva:edit-2-fill' },
    SUBMITTED:   { label: 'Submitted',   color: '#00AB55', bg: 'rgba(0,171,85,0.12)',     icon: 'eva:checkmark-circle-2-fill' },
    EXPIRED:     { label: 'Expired',     color: '#919EAB', bg: 'rgba(145,158,171,0.12)', icon: 'eva:alert-circle-fill' },
    REVOKED:     { label: 'Revoked',     color: '#B72136', bg: 'rgba(183,33,54,0.12)',    icon: 'eva:close-circle-fill' },
};

const getExpiryText = (expiresAt) => {
    const m = parseApiDate(expiresAt);
    if (!m) return null;
    const now = moment();
    if (m.isBefore(now)) return { text: 'Expired', color: '#919EAB' };
    const diffHours = m.diff(now, 'hours', true);
    if (diffHours < 24) return { text: `Expires in ${Math.ceil(diffHours)}h`, color: '#FF4842' };
    const days = Math.floor(m.diff(now, 'days', true));
    return { text: `Expires in ${days}d`, color: '#078DEE' };
};

// ─── Channel configuration ────────────────────────────────────────────────────
const CHANNEL_CONFIG = {
    EMAIL:    { icon: 'eva:email-fill',           label: 'Email',    color: '#078DEE' },
    WHATSAPP: { icon: 'ic:baseline-whatsapp',     label: 'WhatsApp', color: '#25D366' },
    SMS:      { icon: 'eva:message-square-fill',  label: 'SMS',      color: '#FFAB00' },
    COPY:     { icon: 'eva:copy-fill',            label: 'Link',     color: '#919EAB' },
};

// ─── Form type badge ───────────────────────────────────────────────────────────
const FORM_TYPE_COLORS = {
    MEDICAL_HISTORY:       '#855CF8',
    COMMUNICATION_CONSENT: '#078DEE',
    PHOTOGRAPHY_CONSENT:   '#FF6B6B',
    RADIOGRAPHY_CONSENT:   '#FF8C00',
    SURVEY:                '#26C6DA',
    TREATMENT_PLAN:        '#00AB55',
    PATIENT_INTAKE:        '#FF6B6B',
    REFERRAL:              '#FF8C00',
    OTHER:                 '#919EAB',
    CONSENT:               '#637381',
};

export default function FormRequestCard({ request, onCopyLink, onRevoke, onResend, onRemind, onViewSubmission }) {
    const status = requestStatus(request);
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    const expiryInfo = getExpiryText(request.expires_at);
    const formType = request.form?.form_type || 'CONSENT';
    const typeColor = FORM_TYPE_COLORS[formType] || '#637381';
    const formName = request.form?.name || 'Consent Form';
    const channelKey = request.channel || 'COPY';
    const channelCfg = CHANNEL_CONFIG[channelKey] || CHANNEL_CONFIG.COPY;
    const submissionCount = request.submission_count || 0;
    const maxSubmissions = request.max_submissions || 2;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: '10px',
                border: '1px solid rgba(145,158,171,0.16)',
                bgcolor: '#fafafa',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 2px 8px rgba(145,158,171,0.16)' },
            }}
        >
            {/* Form type icon */}
            <Box sx={{
                width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(typeColor, 0.1),
            }}>
                <Iconify icon="solar:document-text-bold-duotone" width={20} sx={{ color: typeColor }} />
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', truncate: true, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formName}
                    </Typography>
                    <Chip
                        label={formType.replace(/_/g, ' ')}
                        size="small"
                        sx={{ fontSize: '10px', height: 18, px: 0.5, bgcolor: alpha(typeColor, 0.08), color: typeColor, fontWeight: 600, border: `1px solid ${alpha(typeColor, 0.2)}` }}
                    />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Iconify icon={channelCfg.icon} width={12} sx={{ color: channelCfg.color }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                            {request.last_sent_at ? 'Last sent' : 'Created'} {formatDate(request.last_sent_at || request.created_at)}
                        </Typography>
                    </Stack>
                    {expiryInfo && status === 'PENDING' && (
                        <Typography variant="caption" sx={{ color: expiryInfo.color, fontSize: '11px', fontWeight: 600 }}>
                            {expiryInfo.text}
                        </Typography>
                    )}
                    {status === 'SUBMITTED' && submissionCount > 0 && (
                        <Typography variant="caption" sx={{ color: '#919EAB', fontSize: '11px' }}>
                            {submissionCount}/{maxSubmissions} submissions
                        </Typography>
                    )}
                </Stack>
            </Box>

            {/* Status badge */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.4, borderRadius: '6px',
                bgcolor: cfg.bg,
            }}>
                <Iconify icon={cfg.icon} width={13} sx={{ color: cfg.color }} />
                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: cfg.color }}>
                    {cfg.label}
                </Typography>
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                {(status === 'PENDING' || status === 'OPENED' || status === 'IN_PROGRESS') && onCopyLink && (
                    <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => onCopyLink(request)} sx={{ color: 'text.secondary' }}>
                            <Iconify icon="eva:copy-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
                {status === 'SUBMITTED' && onViewSubmission && (
                    <Tooltip title="View submission">
                        <IconButton size="small" onClick={() => onViewSubmission(request)} sx={{ color: common.mintyZest }}>
                            <Iconify icon="eva:eye-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
                {OUTSTANDING_FORM_STATUSES.includes(status) && request.sequence_eligible !== false && onRemind && (
                    <Tooltip title="Remind using existing link"><IconButton size="small" onClick={() => onRemind(request)} aria-label="Remind using existing link"><Iconify icon="eva:bell-fill" width={16} /></IconButton></Tooltip>
                )}
                {['EXPIRED', 'REVOKED', 'SUBMITTED'].includes(status) && onResend && (
                    <Tooltip title={status === 'SUBMITTED' ? 'Request a new response' : 'Replace link'}>
                        <IconButton size="small" onClick={() => onResend(request)} sx={{ color: '#078DEE' }}>
                            <Iconify icon="eva:refresh-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
                {(status === 'PENDING' || status === 'OPENED' || status === 'IN_PROGRESS') && onRevoke && (
                    <Tooltip title="Revoke">
                        <IconButton size="small" onClick={() => onRevoke(request)} sx={{ color: '#B72136' }}>
                            <Iconify icon="eva:close-circle-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

FormRequestCard.propTypes = {
    request:          PropTypes.object.isRequired,
    onCopyLink:       PropTypes.func,
    onRevoke:         PropTypes.func,
    onResend:         PropTypes.func,
    onRemind:         PropTypes.func,
    onViewSubmission: PropTypes.func,
};

FormRequestCard.defaultProps = {
    onCopyLink:       null,
    onRevoke:         null,
    onResend:         null,
    onRemind:         null,
    onViewSubmission: null,
};
